import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { Badge } from '@scramble/ui';
import { prettyMoney } from '@scramble/utils';
import { IncreasedIncomeModal } from './modal';
import {
  INCREASED_INCOME_FEES,
  INCREASED_INCOME_TYPES,
  LAYOUT_TYPE,
} from './constants';
import Icon from '@scramble/design-system/icons/upload-03.svg';

// Cards
import { DashboardBonusCard } from '@/pages/DashboardPage/components/DashboardBonusCard';
import { BannerCard } from '@/containers/OffersBanner/card/BannerCard';
import { OfferCard } from '@/containers/OffersBanner/card/OfferCard';

export const IncreasedIncome = ({ data, layout }) => {
  const { t } = useTranslation();
  const { openSingleModal } = useModal();

  if (!data) return null;

  const getDescription = () => {
    const hasTopUpAndNoInvest =
      data.current.total_topup >= data.current.required_total_topup;

    const translationData = {
      required_investments: prettyMoney(data.current.required_investments),
      required_investments_to_10000: prettyMoney(
        data.current.required_investments_to_10000,
      ),
      required_monthly_top_ups: prettyMoney(
        data.current.required_monthly_top_ups,
      ),
    };

    if (
      [
        INCREASED_INCOME_TYPES.keep_benefiting_save_type_a,
        INCREASED_INCOME_TYPES.keep_benefiting_save_type_b,
      ].includes(data.status) &&
      hasTopUpAndNoInvest
    ) {
      return t(`affiliate:increased_income.${data.status}_invest`, translationData);
    }

    return t(`affiliate:increased_income.${data.status}`, translationData);
  };

  const isActivated =
    (data.current.total_topup >= data.current.required_total_topup &&
      data.current.total_invested >= data.current.required_total_invested) ||
    data.current.active_investment_balance >=
      data.current.required_active_investment_balance;

  const props = {
    static_title: t('affiliate:increased_income.static_title'),
    title: t('affiliate:increased_income.title'),
    desc: getDescription(),
    modal: <IncreasedIncomeModal data={data} />,
    icon: Icon,
    badge: t('affiliate:increased_income.percentage', {
      percentage: INCREASED_INCOME_FEES[data.status],
    }),
    onLearnMore: () => openSingleModal(MODAL_IDS.increased_income),
  };

  switch (layout) {
    case LAYOUT_TYPE.dashboard_desktop_card:
      return (
        <DashboardBonusCard
          title={props.static_title}
          text={props.desc}
          modal={props.modal}
          icon={<Badge label={props.badge} type="neutral" appearance="filled" />}
          footer={
            <button type="button" onClick={props.onLearnMore}>
              {t('affiliate:increased_income.learn_more')}
            </button>
          }
        />
      );
    case LAYOUT_TYPE.investing_slider_card:
      return <BannerCard {...props} />;
    case LAYOUT_TYPE.investing_offer_card:
      return (
        <OfferCard {...props}>
          {isActivated && (
            <Badge
              label={t('affiliate:increased_income.activated')}
              type="neutral"
              appearance="filled"
              size="medium"
            />
          )}
        </OfferCard>
      );
    default:
      return null;
  }
};
